import {
  FormEvent,
  ReactNode,
  useEffect,
  useState,
} from "react";

import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  account,
  config,
  ID,
} from "../lib/appwrite";

import {
  createMyProfile,
  discardUnprofiledAccount,
} from "../lib/webAppApi";

import { useAuth } from "../context/AuthContext";

function errorMessage(
  error: unknown,
  fallback: string,
) {
  return error instanceof Error && error.message
    ? error.message
    : fallback;
}

function Message({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  if (!text) {
    return null;
  }

  return (
    <div
      className={
        error
          ? "message error"
          : "message success"
      }
      role={error ? "alert" : "status"}
    >
      {text}
    </div>
  );
}

function PasswordInput({
  value,
  onChange,
  autoComplete,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  placeholder?: string;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="password-field">
      <input
        type={visible ? "text" : "password"}
        value={value}
        minLength={8}
        maxLength={256}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onChange={(event) =>
          onChange(event.target.value)
        }
        required
      />

      <button
        type="button"
        className="password-toggle"
        aria-label={
          visible
            ? "Hide password"
            : "Show password"
        }
        aria-pressed={visible}
        onClick={() =>
          setVisible((current) => !current)
        }
      >
        {visible ? "Hide" : "Show"}
      </button>
    </div>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [displayName, setDisplayName] =
    useState("");

  const [username, setUsername] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [accepted, setAccepted] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  function updateUsername(value: string) {
    const normalised = value
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9._]/g, "")
      .slice(0, 30);

    setUsername(normalised);
  }

  async function submit(
    event: FormEvent,
  ) {
    event.preventDefault();

    const cleanName =
      displayName.trim();

    const cleanUsername = username
      .trim()
      .toLowerCase();

    const cleanEmail = email
      .trim()
      .toLowerCase();

    setMessage("");

    if (cleanName.length < 2) {
      setMessage(
        "Enter a display name with at least 2 characters.",
      );
      return;
    }

    if (
      !/^[a-z0-9](?:[a-z0-9._]{1,28}[a-z0-9])?$/.test(
        cleanUsername,
      )
    ) {
      setMessage(
        "Username must contain 3–30 characters. Use letters, numbers, dots or underscores, and do not start or end with a dot or underscore.",
      );
      return;
    }

    if (password.length < 8) {
      setMessage(
        "Your password must contain at least 8 characters.",
      );
      return;
    }

    if (!accepted) {
      setMessage(
        "You must accept the Terms of Service and Privacy Policy.",
      );
      return;
    }

    setBusy(true);

    let accountCreated = false;
    let sessionCreated = false;
    let profileCreated = false;

    try {
      await account.create({
        userId: ID.unique(),
        email: cleanEmail,
        password,
        name: cleanName,
      });

      accountCreated = true;

      await account.createEmailPasswordSession({
        email: cleanEmail,
        password,
      });

      sessionCreated = true;

      await createMyProfile(
        cleanName,
        cleanUsername,
      );

      profileCreated = true;

      await account.createEmailVerification({
        url:
          `${config.siteUrl}` +
          "/saradium/verify",
      });

      await refresh();

      navigate(
        "/check-email",
        {
          replace: true,
          state: {
            email: cleanEmail,
          },
        },
      );
    } catch (error) {
      /*
       * If Appwrite created the account but SARADIUM could
       * not create its profile, remove that incomplete
       * account through the secure backend.
       */
      if (
        accountCreated &&
        sessionCreated &&
        !profileCreated
      ) {
        try {
          await discardUnprofiledAccount();
          sessionCreated = false;
        } catch {
          /*
           * The original registration error is more useful
           * to the user than a cleanup error.
           */
        }
      }

      if (sessionCreated && !profileCreated) {
        try {
          await account.deleteSession({
            sessionId: "current",
          });
        } catch {
          // The session may already be unavailable.
        }
      }

      setMessage(
        errorMessage(
          error,
          "Unable to create your SARADIUM account.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Create your account"
      subtitle="Create your SARADIUM identity and organise your day across mobile and web."
    >
      <form
        onSubmit={submit}
        className="form"
      >
        <label>
          Display name

          <input
            value={displayName}
            minLength={2}
            maxLength={64}
            autoComplete="name"
            placeholder="Your name"
            onChange={(event) =>
              setDisplayName(
                event.target.value,
              )
            }
            required
          />
        </label>

        <label>
          Username

          <div className="username-field">
            <span aria-hidden="true">@</span>

            <input
              value={username}
              minLength={3}
              maxLength={30}
              autoComplete="username"
              spellCheck={false}
              placeholder="yourusername"
              onChange={(event) =>
                updateUsername(
                  event.target.value,
                )
              }
              required
            />
          </div>

          <small className="field-help">
            This becomes your public SARADIUM
            identity. It must be unique.
          </small>
        </label>

        <label>
          Email address

          <input
            type="email"
            value={email}
            maxLength={320}
            autoComplete="email"
            placeholder="you@example.com"
            onChange={(event) =>
              setEmail(event.target.value)
            }
            required
          />
        </label>

        <label>
          Password

          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            placeholder="At least 8 characters"
          />

          <small className="field-help">
            Use a long, unique password that you
            do not use on another website.
          </small>
        </label>

        <label className="auth-consent">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) =>
              setAccepted(
                event.target.checked,
              )
            }
            required
          />

          <span>
            I agree to the{" "}
            <Link to="/terms">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy">
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <Message
          text={message}
          error
        />

        <button
          type="submit"
          className="button primary"
          disabled={busy}
        >
          {busy
            ? "Creating account…"
            : "Create account"}
        </button>
      </form>

      <p className="form-foot">
        Already registered?{" "}
        <Link to="/login">
          Sign in
        </Link>
      </p>
    </AuthCard>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  async function submit(
    event: FormEvent,
  ) {
    event.preventDefault();
    setBusy(true);
    setMessage("");

    try {
      await account.createEmailPasswordSession({
        email: email.trim().toLowerCase(),
        password,
      });

      const currentUser =
        await account.get();

      if (!currentUser.emailVerification) {
        await refresh();

        navigate(
          "/check-email",
          {
            replace: true,
            state: {
              email: currentUser.email,
            },
          },
        );
        return;
      }

      await refresh();

      const requestedPath =
        (
          location.state as {
            from?: string;
          } | null
        )?.from;

      const safePath =
        requestedPath?.startsWith("/")
          ? requestedPath
          : "/app/routines";

      navigate(
        safePath,
        {
          replace: true,
        },
      );
    } catch (error) {
      setMessage(
        errorMessage(
          error,
          "Unable to sign in.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Welcome back"
      subtitle="Sign in to continue to your SARADIUM web application."
    >
      <form
        onSubmit={submit}
        className="form"
      >
        <label>
          Email address

          <input
            type="email"
            value={email}
            maxLength={320}
            autoComplete="email"
            placeholder="you@example.com"
            onChange={(event) =>
              setEmail(event.target.value)
            }
            required
          />
        </label>

        <label>
          Password

          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            placeholder="Your password"
          />
        </label>

        <Message
          text={message}
          error
        />

        <button
          type="submit"
          className="button primary"
          disabled={busy}
        >
          {busy
            ? "Signing in…"
            : "Sign in"}
        </button>
      </form>

      <div className="form-foot">
        <Link to="/forgot-password">
          Forgot password?
        </Link>

        <br />

        New to SARADIUM?{" "}
        <Link to="/register">
          Create account
        </Link>
      </div>
    </AuthCard>
  );
}

export function CheckEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const suppliedEmail =
    (
      location.state as {
        email?: string;
      } | null
    )?.email;

  const [email, setEmail] =
    useState(suppliedEmail ?? "");

  const [message, setMessage] =
    useState("Open the verification email and select its verification button.");

  const [error, setError] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  useEffect(() => {
    void checkStatus(false);
  }, []);

  async function checkStatus(showMessage = true) {
    setBusy(true);

    try {
      const currentUser = await account.get();
      setEmail(currentUser.email);

      if (currentUser.emailVerification) {
        await refresh();
        navigate("/app/today", { replace: true });
        return;
      }

      if (showMessage) {
        setError(false);
        setMessage("Your email is not verified yet. Open the newest SARADIUM verification email and select Verify email.");
      }
    } catch {
      setError(true);
      setMessage("Your verification session expired. Sign in again to resend the email.");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setMessage("");

    try {
      await account.createEmailVerification({
        url:
          `${config.siteUrl}` +
          "/saradium/verify",
      });

      setError(false);
      setMessage(
        "A new verification email has been sent. Check your inbox and spam folder.",
      );
    } catch (caught) {
      setError(true);
      setMessage(
        errorMessage(
          caught,
          "Unable to resend the verification email.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Check your email"
      subtitle={
        email
          ? `We sent a verification link to ${email}.`
          : "We sent a verification link to your email address."
      }
    >
      <p className="muted">
        Open the message and select the
        verification button. You can then return
        to SARADIUM and sign in.
      </p>

      <Message
        text={message}
        error={error}
      />

      <div className="auth-actions">
        <button
          type="button"
          className="button primary"
          disabled={busy}
          onClick={() => void checkStatus(true)}
        >
          {busy ? "Checking…" : "I have verified my email"}
        </button>

        <button
          type="button"
          className="button secondary"
          disabled={busy}
          onClick={resend}
        >
          Resend verification email
        </button>
      </div>

      <p className="form-foot">
        <Link to="/login">Use another account</Link>
      </p>
    </AuthCard>
  );
}

export function VerifyPage() {
  const params =
    new URLSearchParams(
      window.location.search,
    );

  const userId =
    params.get("userId");

  const secret =
    params.get("secret");

  const hasParameters =
    Boolean(userId && secret);

  const [state, setState] =
    useState<
      "ready" |
      "busy" |
      "done" |
      "error"
    >(
      hasParameters
        ? "ready"
        : "error",
    );

  const [message, setMessage] =
    useState(
      hasParameters
        ? "Confirm your email address to finish setting up SARADIUM."
        : "This verification link is incomplete or invalid.",
    );

  async function verify() {
    if (!userId || !secret) {
      return;
    }

    setState("busy");

    try {
      await account.updateEmailVerification({
        userId,
        secret,
      });

      setState("done");

      setMessage(
        "Your email has been verified successfully.",
      );
    } catch (error) {
      setState("error");

      setMessage(
        errorMessage(
          error,
          "Email verification failed.",
        ),
      );
    }
  }

  return (
    <AuthCard
      title="Email verification"
      subtitle={message}
    >
      {state === "error" && (
        <Message
          text={message}
          error
        />
      )}

      {state !== "done" && (
        <button
          type="button"
          className="button primary"
          disabled={state !== "ready"}
          onClick={verify}
        >
          {state === "busy"
            ? "Verifying…"
            : "Verify email"}
        </button>
      )}

      {state === "done" && (
        <Link
          className="button primary"
          to="/login"
        >
          Continue to sign in
        </Link>
      )}
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  async function submit(
    event: FormEvent,
  ) {
    event.preventDefault();
    setBusy(true);
    setMessage("");

    try {
      await account.createRecovery({
        email: email.trim().toLowerCase(),
        url:
          `${config.siteUrl}` +
          "/saradium/recovery",
      });

      setError(false);

      setMessage(
        "If that account exists, a password-recovery email has been sent.",
      );
    } catch {
      /*
       * Use the same response whether an account exists
       * or not to avoid exposing registered addresses.
       */
      setError(false);

      setMessage(
        "If that account exists, a password-recovery email has been sent.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Reset your password"
      subtitle="Enter the email connected to your SARADIUM account."
    >
      <form
        onSubmit={submit}
        className="form"
      >
        <label>
          Email address

          <input
            type="email"
            value={email}
            maxLength={320}
            autoComplete="email"
            placeholder="you@example.com"
            onChange={(event) =>
              setEmail(event.target.value)
            }
            required
          />
        </label>

        <Message
          text={message}
          error={error}
        />

        <button
          type="submit"
          className="button primary"
          disabled={busy}
        >
          {busy
            ? "Sending…"
            : "Send recovery email"}
        </button>
      </form>

      <p className="form-foot">
        <Link to="/login">
          Return to sign in
        </Link>
      </p>
    </AuthCard>
  );
}

export function RecoveryPage() {
  const params =
    new URLSearchParams(
      window.location.search,
    );

  const userId =
    params.get("userId");

  const secret =
    params.get("secret");

  const [password, setPassword] =
    useState("");

  const [confirm, setConfirm] =
    useState("");

  const [message, setMessage] =
    useState(
      userId && secret
        ? ""
        : "This recovery link is incomplete or invalid.",
    );

  const [error, setError] =
    useState(
      !(userId && secret),
    );

  const [done, setDone] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  async function submit(
    event: FormEvent,
  ) {
    event.preventDefault();
    setMessage("");

    if (password.length < 8) {
      setError(true);

      setMessage(
        "Your new password must contain at least 8 characters.",
      );
      return;
    }

    if (password !== confirm) {
      setError(true);

      setMessage(
        "The passwords do not match.",
      );
      return;
    }

    if (!userId || !secret) {
      setError(true);

      setMessage(
        "This recovery link is incomplete or invalid.",
      );
      return;
    }

    setBusy(true);

    try {
      await account.updateRecovery({
        userId,
        secret,
        password,
      });

      setDone(true);
      setError(false);

      setPassword("");
      setConfirm("");

      setMessage(
        "Your password has been updated successfully.",
      );
    } catch (error) {
      setError(true);

      setMessage(
        errorMessage(
          error,
          "Password recovery failed.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Choose a new password"
      subtitle="Use a strong password that you have not used elsewhere."
    >
      <Message
        text={message}
        error={error}
      />

      {!done && userId && secret && (
        <form
          className="form"
          onSubmit={submit}
        >
          <label>
            New password

            <PasswordInput
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </label>

          <label>
            Confirm new password

            <PasswordInput
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              placeholder="Enter it again"
            />
          </label>

          <button
            type="submit"
            className="button primary"
            disabled={busy}
          >
            {busy
              ? "Updating…"
              : "Update password"}
          </button>
        </form>
      )}

      {done && (
        <Link
          className="button primary"
          to="/login"
        >
          Sign in
        </Link>
      )}
    </AuthCard>
  );
}

function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <section className="auth-wrap">
      <div className="auth-card">
        <Link
          className="mini-brand"
          to="/"
          aria-label="SARADIUM home"
        >
          SARADIUM
        </Link>

        <h1>{title}</h1>

        <p className="muted">
          {subtitle}
        </p>

        {children}
      </div>
    </section>
  );
}
